import { BeatResolution, Key, Scale, StemType } from '@arpeggi/shared'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { useTreeState } from '../../../stores/Root'
import { IAudioStemStore } from '../../../stores/song/stem/AudioStemStore'
import { IDrumStemStore } from '../../../stores/song/stem/DrumStemStore'
import { IInstrumentStemStore } from '../../../stores/song/stem/InstrumentStemStore'
import { IStemStore } from '../../../stores/song/StemStore'
import { CloseButton, Row, TooltipIconButton } from '../../core/Components'
import Dropdown from '../../core/Dropdown'
import { ScissorsIcon, TrashIcon } from '../../core/icons'
import { blue5, gray15 } from '../../platform/color'
import { ReverseIcon } from '../../platform/icons'
import { InputContainer, INPUT_CONTAINER_BACKGROUND_COLOR, Label } from './common'
import SnapToggle from './SnapToggle'
import Stretch from './Stretch'
import Transpose from './Transpose'

const RESOLUTION_OPTIONS = [
  { order: 1, value: BeatResolution.QUARTER_NOTE, label: '1/4' },
  { order: 2, value: BeatResolution.EIGHTH_NOTE, label: '1/8' },
  { order: 4, value: BeatResolution.SIXTEENTH_NOTE, label: '1/16' },
  { order: 3, value: BeatResolution.EIGHTH_NOTE_TRIPLETS, label: '1/8 triplet' },
  { order: 5, value: BeatResolution.SIXTEENTH_NOTE_TRIPLETS, label: '1/16 triplet' }
]

const SCALE_OPTIONS = [
  { value: Scale.CHROMATIC, label: 'Full' },
  { value: Scale.MAJOR, label: 'Major' },
  { value: Scale.MINOR, label: 'Minor' }
]

const KEY_OPTIONS = [
  { value: Key.C, label: 'C' },
  { value: Key.C_SHARP, label: 'C#' },
  { value: Key.D, label: 'D' },
  { value: Key.D_SHARP, label: 'D#' },
  { value: Key.E, label: 'E' },
  { value: Key.F, label: 'F' },
  { value: Key.F_SHARP, label: 'F#' },
  { value: Key.G, label: 'G' },
  { value: Key.G_SHARP, label: 'G#' },
  { value: Key.A, label: 'A' },
  { value: Key.A_SHARP, label: 'A#' },
  { value: Key.B, label: 'B' }
]

interface Props {
  close: () => void
  stem: IStemStore
  selectedDrumTrack: number
  isDrums: boolean
}
const STCControls: FC<Props> = ({ close, stem, selectedDrumTrack, isDrums }) => {
  const { studio } = useTreeState()
  const loopIndex = stem.getLoopIndex(studio.selectedSection) - 1
  const loopRef = loopIndex >= 0 ? stem.loops[loopIndex] : null
  const isRemixed = stem.isRemixed

  const selectedClip =
    stem.type() === StemType.AUDIO &&
    (stem as IAudioStemStore).clips.filter((clip) => clip.uniqueId === studio.selectedClipIDs[0])[0]

  const removeLoop = () => {
    if (stem.type() === StemType.AUDIO) {
      return
    }
    const stemWithLoopsStore = stem as IInstrumentStemStore | IDrumStemStore
    stemWithLoopsStore.clearLoop(loopIndex)
  }

  const margin = '20px'
  return (
    <Row style={{ flex: '1', flexShrink: 0 }} verticalCenter justifyContentEnd>
      {!isRemixed && stem.type() === StemType.INSTRUMENT && loopRef !== null && <SnapToggle />}
      {/* Key Dropdown is disabled until further notice... */}
      <Row verticalCenter css={{ marginLeft: margin }}>
        {!isRemixed &&
          !isDrums &&
          loopRef !== null &&
          loopRef.melodicLoop.melodicSettings.scale !== Scale.CHROMATIC && (
            <>
              <Label dark>Key</Label>
              <InputContainer moreRound>
                <Dropdown
                  value={loopRef.melodicLoop.melodicSettings.key}
                  options={KEY_OPTIONS}
                  onSelect={(selectedKey) => {
                    studio.disableAudioUpdates()
                    loopRef.melodicLoop.updateKey(selectedKey.value)
                    studio.enableAudioUpdates()
                  }}
                  slim
                />
              </InputContainer>
            </>
          )}
      </Row>

      {/* Scale Dropdown disabled until further notice... */}
      <Row verticalCenter css={{ marginLeft: margin }}>
        {!isRemixed && !isDrums && loopRef !== null && (
          <>
            <Label dark>Scale</Label>
            <InputContainer moreRound>
              <Dropdown
                value={loopRef.melodicLoop.melodicSettings.scale}
                options={SCALE_OPTIONS}
                onSelect={(selectedScale) => {
                  studio.disableAudioUpdates()
                  loopRef.melodicLoop.updateScale(selectedScale.value)
                  studio.enableAudioUpdates()
                }}
                slim
              />
            </InputContainer>
          </>
        )}
      </Row>

      {/* Resolution Dropdown */}
      <Row verticalCenter css={{ marginLeft: margin }}>
        {!isRemixed &&
          !(isDrums && loopRef && loopRef.drumLoop.subLoops.length === 0) &&
          loopRef !== null && (
            <>
              <Label dark>Grid</Label>
              <InputContainer moreRound>
                <Dropdown
                  value={
                    isDrums
                      ? loopRef.drumLoop.subLoops[selectedDrumTrack].settings.resolution
                      : loopRef.melodicLoop.settings.resolution
                  }
                  options={_.orderBy(RESOLUTION_OPTIONS, (a) => a.order, 'asc')}
                  onSelect={(selectedResolution) => {
                    studio.disableAudioUpdates()
                    if (isDrums) {
                      loopRef.drumLoop.subLoops[selectedDrumTrack].updateResolution(
                        selectedResolution.value
                      )
                    } else {
                      loopRef.melodicLoop.updateResolution(selectedResolution.value)
                    }
                    studio.enableAudioUpdates()
                  }}
                  slim
                />
              </InputContainer>
            </>
          )}
      </Row>

      {/* Clear Notes Button */}
      <Row verticalCenter css={{ marginLeft: margin }}>
        {!isRemixed && loopRef !== null && (
          <TooltipIconButton
            tooltipText="Clear Notes"
            onClick={removeLoop}
            css={{
              backgroundColor: INPUT_CONTAINER_BACKGROUND_COLOR,
              '&:hover': { opacity: '70%' }
            }}
            round
          >
            <TrashIcon height="16px" width="16px" />
          </TooltipIconButton>
        )}
      </Row>

      {/* Delete Clip */}
      {stem.type() === StemType.AUDIO && studio.selectedClipIDs.length > 0 && (
        <Row verticalCenter css={{ marginLeft: margin }}>
          <TooltipIconButton
            tooltipText={`Delete Clip${studio.selectedClipIDs.length > 1 ? 's' : ''}`}
            onClick={() => studio.deleteSelectedClips()}
          >
            <TrashIcon height="16px" width="16px" style={{ color: gray15 }} />
          </TooltipIconButton>
        </Row>
      )}

      {/* Cut */}
      {stem.type() === StemType.AUDIO && studio.selectedClipIDs.length === 1 && (
        <Row verticalCenter css={{ marginLeft: margin }}>
          <TooltipIconButton
            tooltipText="Cut"
            onClick={() => studio.setMouseAction(studio.mouseAction === 'CUT' ? 'DEFAULT' : 'CUT')}
          >
            <ScissorsIcon
              height="16px"
              width="16px"
              style={{ color: studio.mouseAction === 'CUT' ? blue5 : gray15 }}
            />
          </TooltipIconButton>
        </Row>
      )}

      {/* Reverse */}
      {stem.type() === StemType.AUDIO && studio.selectedClipIDs.length === 1 && (
        <Row verticalCenter css={{ marginLeft: margin }}>
          <TooltipIconButton
            tooltipText="Reverse"
            onClick={() => selectedClip.updateReverse(!selectedClip?.reverse, studio.song.tempo)}
          >
            <ReverseIcon
              height="16px"
              width="16px"
              style={{ color: selectedClip?.reverse ? blue5 : gray15 }}
            />
          </TooltipIconButton>
        </Row>
      )}

      {/* Stretch */}
      {stem.type() === StemType.AUDIO && selectedClip && selectedClip.canBeStretchedOrTransposed && (
        <Row verticalCenter css={{ marginLeft: margin }}>
          <Stretch
            value={selectedClip.warp}
            onChange={selectedClip.updateWarp}
            onMatchClick={() => selectedClip.matchTempo(studio.song.tempo)}
            showMatchButton={selectedClip.getSample().getArpMedia()?.metadata?.bpm > 0}
          />
        </Row>
      )}

      {/* Tranpose */}
      {stem.type() === StemType.AUDIO && selectedClip && selectedClip.canBeStretchedOrTransposed && (
        <Row verticalCenter>
          <Transpose
            value={selectedClip.transpose}
            onChange={selectedClip.updateTranspose}
            onTuneClick={() => studio.song.tuneClipToSong(selectedClip.uniqueId)}
            // showTuneButton // TODO: Add this back when it functions correctly
          />
        </Row>
      )}

      <CloseButton close={close} />
    </Row>
  )
}

export default observer(STCControls)
